import { saveAs } from "file-saver";

import { FinancialFilterValues, NfeNotice, NfeRow } from "./types";

export const HUBSOFT_NFE_STATUSES = [
  "aguardando_efetivacao",
  "aguardando_cancelamento",
  "cancelada",
  "denegada",
  "efetivada",
  "em_transmissao",
  "retransmissao",
  "inutilizada",
  "outro",
  "aguardando_envio",
  "rejeitada",
  "timeout",
] as const;

export const TOP_NFE_TIPOS = ["NF2122", "REC", "NFe", "NFSe", "NFCOM"] as const;

const HUBSOFT_STATUS_SET = new Set<string>(HUBSOFT_NFE_STATUSES);

export interface NfeIntegrationConfig {
  value: string;
  status: false | "hubsoft" | "text";
  tipo: boolean;
  situacao: boolean;
  modelo: boolean;
  detail: boolean;
  download: boolean;
  requiresTipo: boolean;
  sendDetailContext: boolean;
  extraColumns: Array<"tipo" | "id_fatura">;
}

export const NFE_INTEGRATIONS: NfeIntegrationConfig[] = [
  {
    value: "hubsoft",
    status: "hubsoft",
    tipo: false,
    situacao: false,
    modelo: false,
    detail: false,
    download: false,
    requiresTipo: false,
    sendDetailContext: false,
    extraColumns: [],
  },
  {
    value: "top",
    status: false,
    tipo: true,
    situacao: true,
    modelo: false,
    detail: true,
    download: true,
    requiresTipo: true,
    sendDetailContext: true,
    extraColumns: ["tipo", "id_fatura"],
  },
  {
    value: "ixc",
    status: "text",
    tipo: false,
    situacao: true,
    modelo: true,
    detail: true,
    download: false,
    requiresTipo: false,
    sendDetailContext: false,
    extraColumns: ["tipo", "id_fatura"],
  },
];

export function getNfeIntegration(value: string | null | undefined) {
  if (!value) return undefined;
  return NFE_INTEGRATIONS.find((item) => item.value === value);
}

export function formatNfeAmount(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatNfeStatus(value: unknown, translate: (key: string) => string) {
  if (value === null || value === undefined || value === "") return "—";
  const status = String(value);
  if (HUBSOFT_STATUS_SET.has(status)) return translate(`financial_admin.nfe_status.${status}`);
  return status;
}

export function formatIsoDate(value: string | null | undefined) {
  if (!value) return "—";
  const date = value.slice(0, 10);
  const [year, month, day] = date.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

export function noticeLines(items: NfeNotice[] | undefined) {
  return (items ?? [])
    .map((item) => {
      const reason = item.reason?.trim();
      if (!reason) return "";
      return item.agreement_name ? `${item.agreement_name}: ${reason}` : reason;
    })
    .filter(Boolean);
}

export function formatFinancialError(error: unknown, fallback: string) {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data == null) return fallback;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data !== "object") return fallback;

  const record = data as Record<string, unknown>;
  if (typeof record.detail === "string" && record.detail.trim()) return record.detail;
  if (Array.isArray(record.detail)) {
    const detail = record.detail.map(String).filter(Boolean).join(" ");
    if (detail) return detail;
  }

  const messages = Object.entries(record).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      const text = value.map(String).filter(Boolean).join(" ");
      return text ? [`${key}: ${text}`] : [];
    }
    if (typeof value === "string" && value.trim()) return [`${key}: ${value}`];
    return [];
  });

  return messages.length ? messages.join(" ") : fallback;
}

function hasValue(value: unknown): value is string | number {
  return value !== null && value !== undefined && value !== "";
}

export function nfeActionKey(row: NfeRow) {
  return [row.integration, row.agreement_id, row.id_nota ?? "", row.tipo ?? ""].join(":");
}

export function toNfeNoteParams(
  row: NfeRow,
  filters: FinancialFilterValues
): Record<string, string | number> | null {
  const config = getNfeIntegration(row.integration);
  if (!config?.detail || !hasValue(row.agreement_id) || !hasValue(row.id_nota)) return null;
  if (config.requiresTipo && !hasValue(row.tipo)) return null;

  const params: Record<string, string | number> = {
    integration: row.integration,
    agreement_id: row.agreement_id,
    id_nota: row.id_nota,
  };

  if (config.requiresTipo && hasValue(row.tipo)) params.tipo = row.tipo;

  if (config.sendDetailContext) {
    if (hasValue(row.id_lote)) params.id_lote = row.id_lote;
    if (hasValue(row.id_empresa)) params.id_empresa = row.id_empresa;
    const year = filters.year === "" ? undefined : Number(filters.year);
    const month = filters.month === "" ? undefined : Number(filters.month);
    if (year) params.year = year;
    if (year && month) params.month = month;
  }

  return params;
}

export function toNfeFileParams(row: NfeRow): Record<string, string | number> | null {
  const config = getNfeIntegration(row.integration);
  if (!config?.download || !hasValue(row.agreement_id) || !hasValue(row.id_nota)) return null;
  if (!hasValue(row.tipo)) return null;

  return {
    integration: row.integration,
    agreement_id: row.agreement_id,
    tipo: row.tipo,
    id_nota: row.id_nota,
  };
}

export function saveBase64File(base64: string, filename: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  saveAs(new Blob([bytes]), filename);
}
